import React, { Component } from 'react';

import mixpanel from 'mixpanel-browser';
import PropTypes from 'prop-types';
import { browserHistory } from 'react-router';

import { CompanyContext } from 'common/containers/CompanyContainer';
import { GetExperimentGroupsContext } from 'common/containers/ExperimentContainer';
import { ViewerContext } from 'common/containers/ViewerContainer';
import withContexts from 'common/util/withContexts';

import cloneElementWithProps from '../core/cloneElementWithProps';

export const TrackMixpanelEventContext = React.createContext();

const MixpanelID = __DEV__
  ? 'c2641a425e9220f785d0be3c55d8ec2c'
  : 'ac70ebddf8b9618e6a8365f61234d2a4';

function loadMixpanelSDK() {
  mixpanel.init(MixpanelID, {
    debug: false,
  });
}

class MixpanelContainer extends Component {
  static propTypes = {
    company: PropTypes.object,
    property: PropTypes.string,
    trackPageViews: PropTypes.bool,
    viewer: PropTypes.object,
  };

  static defaultProps = {
    trackPageViews: false,
  };

  componentDidMount() {
    loadMixpanelSDK();

    this.identifyUser();
    this.trackPageViews();
  }

  componentWillUnmount() {
    if (!this._unlisten) {
      return;
    }

    this._unlisten();
  }

  identifyUser = () => {
    const { getExperimentGroups, viewer } = this.props;
    const experimentGroups = getExperimentGroups();

    if (!viewer?._id) {
      return;
    }

    mixpanel.identify(viewer._id);
    mixpanel.people.set({
      ...experimentGroups,
      ...(viewer.avatarURL && {
        $avatar: viewer.avatarURL,
      }),
      $email: viewer.email,
      $name: viewer.name,
    });

    if (viewer.companies.length === 0) {
      return;
    }

    mixpanel.set_group(
      'company_id',
      viewer.companies.map((company) => company._id)
    );

    viewer.companies.forEach((company) => {
      mixpanel.get_group('company_id', company._id).set({
        $name: company.name,
        subdomain: company.subdomain,
      });
    });
  };

  trackPageViews = () => {
    if (!this.props.trackPageViews) {
      return;
    }

    this._unlisten = browserHistory.listen(this.onRouteChange);
    this.onRouteChange();
  };

  onRouteChange = () => {
    loadMixpanelSDK();

    mixpanel.track('pageview', {
      host: window.location.host,
      href: window.location.href,
      pathname: window.location.pathname,
      property: this.props.property,
      search: window.location.search,
      title: document.title,
    });
  };

  onTrackEvent = (eventName, eventData) => {
    loadMixpanelSDK();

    const { getExperimentGroups } = this.props;
    const experimentGroups = getExperimentGroups();

    mixpanel.track(eventName, {
      property: this.props.property,
      ...eventData,
      ...experimentGroups,
    });
  };

  render() {
    const childProps = Object.assign({}, this.props);
    delete childProps.company;
    delete childProps.getExperimentGroups;
    delete childProps.trackPageViews;
    delete childProps.viewer;
    return (
      <TrackMixpanelEventContext.Provider value={this.onTrackEvent}>
        {cloneElementWithProps(this.props.children, {
          ...childProps,
        })}
      </TrackMixpanelEventContext.Provider>
    );
  }
}

export default withContexts({
  company: CompanyContext,
  getExperimentGroups: GetExperimentGroupsContext,
  viewer: ViewerContext,
})(MixpanelContainer);
